import React, { useState, useEffect, useRef } from 'react'
import axios from '../axios'

// Searches existing courses; if user types a new name and blurs, creates it via API
function CourseAutocomplete({ value, onChange, userInfo }) {
    const [inputVal, setInputVal]   = useState(value?.name || '')
    const [results, setResults]     = useState([])
    const [open, setOpen]           = useState(false)
    const debounceRef               = useRef(null)
    const wrapperRef                = useRef(null)

    // Sync display name when parent value changes (e.g. on initial load)
    useEffect(() => {
        setInputVal(value?.name || '')
    }, [value?.name])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const search = (q) => {
        if (!q.trim()) { setResults([]); return }
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            try {
                const config = { headers: { Authorization: `Bearer ${userInfo.token}` } }
                const { data } = await axios.get(`/api/courses/search/?q=${encodeURIComponent(q)}`, config)
                setResults(data)
                setOpen(true)
            } catch { setResults([]) }
        }, 250)
    }

    const handleChange = (e) => {
        setInputVal(e.target.value)
        search(e.target.value)
    }

    const handleSelect = (course) => {
        setInputVal(course.name)
        setOpen(false)
        onChange({ id: course.id, name: course.name })
    }

    // On blur: if typed name doesn't match selection, create or find the course
    const handleBlur = async () => {
        setOpen(false)
        const trimmed = inputVal.trim()
        if (!trimmed) { onChange(null); return }
        if (value?.name === trimmed) return  // no change
        try {
            const config = { headers: { Authorization: `Bearer ${userInfo.token}`, 'Content-Type': 'application/json' } }
            const { data } = await axios.post('/api/courses/get-or-create/', { name: trimmed }, config)
            onChange({ id: data.id, name: data.name })
        } catch { /* leave as-is */ }
    }

    const inputStyle = {
        fontSize: '0.875rem',
        border: '1.5px solid var(--bnb-border, #e5e7eb)',
        borderRadius: 8,
        padding: '0.5rem 0.75rem',
        width: '100%',
    }

    return (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
            <input
                type="text"
                value={inputVal}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => inputVal && results.length && setOpen(true)}
                placeholder="Type a course name…"
                style={inputStyle}
            />
            {open && results.length > 0 && (
                <ul style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: '#fff', border: '1.5px solid #e5e7eb',
                    borderRadius: 8, zIndex: 999, margin: 0, padding: 0,
                    listStyle: 'none', maxHeight: 200, overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                    {results.map(c => (
                        <li key={c.id}
                            onMouseDown={() => handleSelect(c)}
                            style={{
                                padding: '0.5rem 0.75rem', cursor: 'pointer',
                                fontSize: '0.875rem',
                            }}
                            onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                            onMouseLeave={e => e.target.style.background = '#fff'}
                        >
                            {c.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default CourseAutocomplete
